import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../../lib/motionShim';
import { Scale, ArrowRight, TrendingUp, Target, Zap, Info } from 'lucide-react';

const BMICalculator = () => {
  const [formData, setFormData] = useState({
    gender: 'male',
    height: '',
    weight: '',
    age: '',
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateBMI = () => {
    const height = parseFloat(formData.height) / 100;
    const weight = parseFloat(formData.weight);

    if (!height || !weight || height <= 0 || weight <= 0) return;

    setIsCalculating(true);

    setTimeout(() => {
      const bmi = weight / (height * height);
      const healthyMin = 18.5 * (height * height);
      const healthyMax = 24.9 * (height * height);

      let category = '';
      let categoryClass = '';
      let categoryGradient = '';
      let ringColor = '';

      if (bmi < 18.5) {
        category = 'Underweight';
        categoryClass = 'bmi-underweight';
        categoryGradient = 'from-blue-500 to-cyan-500';
        ringColor = '#06b6d4';
      } else if (bmi < 25) {
        category = 'Normal Weight';
        categoryClass = 'bmi-normal';
        categoryGradient = 'from-emerald-500 to-green-500';
        ringColor = '#10b981';
      } else if (bmi < 30) {
        category = 'Overweight';
        categoryClass = 'bmi-overweight';
        categoryGradient = 'from-amber-500 to-orange-500';
        ringColor = '#f59e0b';
      } else {
        category = 'Obese';
        categoryClass = 'bmi-obese';
        categoryGradient = 'from-red-500 to-rose-500';
        ringColor = '#ef4444';
      }

      setResult({
        bmi: bmi.toFixed(1),
        category,
        categoryClass,
        categoryGradient,
        ringColor,
        healthyMin: healthyMin.toFixed(1),
        healthyMax: healthyMax.toFixed(1),
        percentage: Math.min(100, (bmi / 40) * 100),
      });
      setIsCalculating(false);
    }, 800);
  };

  const scrollToWaitlist = () => {
    document.querySelector('#waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div data-testid="bmi-calculator">
      <motion.div
        layout
        className={`grid gap-8 lg:gap-12 transition-all duration-500 ${result ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-xl mx-auto'
          }`}
      >
        {/* Left - Form */}
        <motion.div layout="position">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-ston-text">
                BMI Calculator
              </h3>
              <p className="text-sm text-ston-text-muted">Know your baseline</p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Gender Toggle */}
            <div>
              <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                Gender
              </label>
              <div className="flex gap-2">
                {['male', 'female'].map((g) => (
                  <motion.button
                    key={g}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, gender: g }))}
                    className={`flex-1 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${formData.gender === g
                        ? 'bg-gradient-to-r from-ston-purple to-violet-600 text-white shadow-lg shadow-ston-purple/30'
                        : 'bg-ston-surface/50 border border-ston-border text-ston-text-secondary hover:border-ston-purple/30 hover:bg-ston-surface'
                      }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`bmi-gender-${g}`}
                  >
                    {g === 'male' ? '♂ Male' : '♀ Female'}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Height and Weight */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Height
                </label>
                <div className="relative group">
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="175"
                    className="w-full bg-ston-surface/50 border border-ston-border rounded-xl px-4 py-3.5 text-ston-text placeholder:text-ston-text-muted/50 focus:border-ston-purple focus:ring-2 focus:ring-ston-purple/20 focus:bg-ston-surface transition-all duration-300"
                    data-testid="bmi-height-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-ston-text-muted bg-ston-surface/80 px-2 py-0.5 rounded-md">cm</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Weight
                </label>
                <div className="relative group">
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="75"
                    className="w-full bg-ston-surface/50 border border-ston-border rounded-xl px-4 py-3.5 text-ston-text placeholder:text-ston-text-muted/50 focus:border-ston-purple focus:ring-2 focus:ring-ston-purple/20 focus:bg-ston-surface transition-all duration-300"
                    data-testid="bmi-weight-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-ston-text-muted bg-ston-surface/80 px-2 py-0.5 rounded-md">kg</span>
                </div>
              </div>
            </div>

            {/* Age */}
            <div>
              <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                Age
              </label>
              <div className="relative group">
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="25"
                  className="w-full bg-ston-surface/50 border border-ston-border rounded-xl px-4 py-3.5 text-ston-text placeholder:text-ston-text-muted/50 focus:border-ston-purple focus:ring-2 focus:ring-ston-purple/20 focus:bg-ston-surface transition-all duration-300"
                  data-testid="bmi-age-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-ston-text-muted bg-ston-surface/80 px-2 py-0.5 rounded-md">years</span>
              </div>
            </div>

            {/* Calculate Button */}
            <motion.button
              onClick={calculateBMI}
              disabled={isCalculating}
              className="w-full mt-1 bg-gradient-to-r from-ston-purple to-violet-600 text-white rounded-xl px-6 py-4 text-base font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-ston-purple/30 disabled:opacity-50 relative overflow-hidden group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="bmi-calculate-btn"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {isCalculating ? (
                <motion.div
                  className="flex items-center justify-center gap-2"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Analyzing...
                </motion.div>
              ) : (
                <span className="relative flex items-center justify-center gap-2">
                  {result ? 'Recalculate BMI' : 'Calculate BMI'}
                  <Zap className="w-4 h-4" />
                </span>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Right - Result */}
        <AnimatePresence>
          {result && (
            <motion.div
              className="flex flex-col items-center justify-center"
              initial={{ opacity: 0, x: 60, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 60, scale: 0.9 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center w-full">
                {/* Category Badge */}
                <motion.div
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r ${result.categoryGradient} mb-5 shadow-lg`}
                  initial={{ scale: 0, rotate: -10 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  data-testid="bmi-category-badge"
                  style={{ boxShadow: `0 4px 20px ${result.ringColor}40` }}
                >
                  <Target className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white tracking-wide">{result.category}</span>
                </motion.div>

                {/* BMI Value with Circular Progress */}
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <svg className="w-full h-full -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="6"
                      className="text-ston-surface"
                    />
                    <motion.circle
                      cx="96"
                      cy="96"
                      r="88"
                      fill="none"
                      stroke="url(#bmiGradient)"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={553}
                      initial={{ strokeDashoffset: 553 }}
                      animate={{ strokeDashoffset: 553 - (553 * result.percentage) / 100 }}
                      transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                    />
                    <defs>
                      <linearGradient id="bmiGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#9333ea" />
                        <stop offset="50%" stopColor="#a855f7" />
                        <stop offset="100%" stopColor="#d946ef" />
                      </linearGradient>
                    </defs>
                  </svg>

                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.div
                      className="text-5xl font-black gradient-text"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5, type: "spring", bounce: 0.4 }}
                      data-testid="bmi-result-value"
                    >
                      {result.bmi}
                    </motion.div>
                    <motion.div
                      className="text-sm text-ston-text-muted font-medium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      BMI Score
                    </motion.div>
                  </div>
                </div>

                {/* Healthy Range Cards */}
                <motion.div
                  className="flex justify-center gap-4 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="text-center px-5 py-3 rounded-xl bg-ston-surface/50 border border-ston-border">
                    <div className="text-lg font-bold text-ston-text">{result.healthyMin}</div>
                    <div className="text-xs text-ston-text-muted">Min Healthy (kg)</div>
                  </div>
                  <div className="w-px bg-ston-border" />
                  <div className="text-center px-5 py-3 rounded-xl bg-ston-surface/50 border border-ston-border">
                    <div className="text-lg font-bold text-ston-text">{result.healthyMax}</div>
                    <div className="text-xs text-ston-text-muted">Max Healthy (kg)</div>
                  </div>
                </motion.div>

                {/* Note */}
                <motion.p
                  className="text-sm text-ston-text-muted max-w-[320px] mx-auto mb-6 flex items-start gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                >
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-ston-purple/50" />
                  <span>BMI is just the start. STON.FIT tracks body composition, strength, and metrics that actually matter.</span>
                </motion.p>

                {/* CTA */}
                <motion.button
                  onClick={scrollToWaitlist}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-ston-purple to-violet-600 text-white rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-ston-purple/30 transition-all"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="bmi-cta-btn"
                >
                  <TrendingUp className="w-4 h-4" />
                  Get Full Body Analysis
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default BMICalculator;
